import React, { useContext, useEffect, useState } from 'react';
import Select from 'react-select';
import isEqual from 'lodash/isEqual';
import { FormContext } from '../metadata/MDForm';
import { api } from '../../api';
import { YearPicker } from './YearPicker';
import { ModelContext } from '../metadata/ModelSource';
import {navigate} from '../../navigation/navigationHistory';
import _ from 'lodash';

const _yearMakeCache = {};
const _manufacturerModelCache = {};

function handleError(err) {
    const statusCode = (err.response ? err.response.status : 0);
    
    if ((statusCode === 503) || (err.message == 'Network Error')) {
        navigate('/server-maintenance');
    }
    throw err;
}

function getYearMakes(year) {
  if (_yearMakeCache[`year${year}`])
    return Promise.resolve(_yearMakeCache[`year${year}`]);

  return api.get(`/vehicleManufacturers?year=${year}`).then((resp) => {
    _yearMakeCache[`year${year}`] = resp.data.manufacturers;
    return (resp.data.manufacturers);
  }).catch(err => handleError(err));
}

function getModels(year, manufacturer) {
  if (_manufacturerModelCache[`${manufacturer}:${year}`])
    return Promise.resolve(_manufacturerModelCache[`${manufacturer}:${year}`]);

  return api
    .get(`/vehicleModels?manufacturer=${manufacturer}&modelYr=${year}`)
    .then((resp) => {
      const result = _.get(resp, 'data.vehicleModelListItems');
      _manufacturerModelCache[`${manufacturer}:${year}`] = result;
      return result;
    }).catch(err => handleError(err));
}

function selectedDiffers(selected, defaultValue) {
  let differs = false;
  if (selected && defaultValue) {
    differs = (selected.value != defaultValue.value);
  } else if (selected || defaultValue) {
    differs = true;
  }

  return (differs);
}

export const VehicleMakeSelect = ({
  year,
  make,
  vinAttr,
  onchange,
  ...props
}) => {
  //const model = useContext(ModelContext);
  const { getValueByAttr } = useContext(FormContext);
  const vin = getValueByAttr(vinAttr);
  const [makes, setMakes] = useState(undefined);
  const defaultMake = (props.value ? props.value : { value: '', label: '' });
  const [selectedOption, setSelectedOption] = useState(
      (make ? { value: make, label: make } : defaultMake)
  );

  const reloading = makes === undefined;

  if ((!make) && selectedDiffers(selectedOption, defaultMake)) setSelectedOption(defaultMake);
  useEffect(() => {
    if (!reloading) {
      setMakes(undefined);
    }
    if (year) {
      getYearMakes(year).then((v) => {
        if (!isEqual(v, makes)) {
          setMakes(v);
        }
      });
    }
  }, [year]);

  const onMakeChange = (selected) => {
    setSelectedOption(selected);
    if (onchange) {
      onchange(selected.value);
    }
  };

  const options = makes
    ? makes.map((item) => {
        return { value: item, label: item, };
      })
    : [ ];

  return (
    <Select
      {...props}
      disabled={vin && vin.length > 16}
      aria-label="Select vehicle make"
      options={options}
      styles={customStyles}
      value={selectedOption}
      onChange={onMakeChange}
      openMenuOnFocus
      placeholder={null}
      components={{
        IndicatorSeparator: () => null,
      }}
      theme={(theme) => ({
        ...theme,
        borderRadius: 4,
        zIndex: 3,
        colors: {
          ...theme.colors,
          primary25: 'rgb(0, 115, 157, 0.1)',
          primary: 'rgba(0, 115, 157, 0.5)',
          neutral30: 'rgba(0, 115, 157, 0.5)',
        },
      })}
    />
  );
};

export const VehicleModelSelect = ({
  year,
  make,
  model,
  vinAttr,
  onchange,
  ...props
}) => {
  const { getValueByAttr, setValueByAttr } = useContext(FormContext);
  const vin = getValueByAttr(vinAttr);
  const [models, setModels] = useState(undefined);
  const defaultModel = (props.value ? props.value : { value: '', label: '' });
  const [selectedOption, setSelectedOption] = useState(
    model ? { value: model, label: model } : defaultModel
  );
  const reloading = models === undefined;
  const onModelChange = (selectValue) => {
    const valParts = selectValue.value.split('/');
    const vinPrefix = valParts[valParts.length - 1];
    setValueByAttr(vinAttr, vinPrefix);
    setSelectedOption(selectValue);
    if (onchange) {
      onchange(selectValue.value);
    }
  };
  if ((!model) && selectedDiffers(selectedOption, defaultModel)) setSelectedOption(defaultModel);
  useEffect( () => {
    if (!reloading) {
      setModels(undefined);
    }
    if (year && make) {
      getModels(year, make).then((v) => {
        if (!isEqual(v, models)) {
          setModels(v);
        }
      });
    }
  }, [year, make])

  const options = getOptions();
  function getOptions() {
      const vehicleModelListItems = models;
      if(vehicleModelListItems && Array.isArray(vehicleModelListItems)){
          return vehicleModelListItems.map((item) => {
            return {
              value: `${item.vehicleModel.model}/${item.vehicleModel.engineSize}(${item.vehicleModel.engineCylinders})/${item.vehicleModel.vinPrefix}`,
              label: `${item.vehicleModel.model}/${item.vehicleModel.engineSize}(${item.vehicleModel.engineCylinders})/${item.vehicleModel.vinPrefix}`,
            };
          })
      }
      return [ ];
  }

  return (
    <Select
      {...props}
      disabled={vin && vin.length > 16}
      aria-label="Select vehicle model"
      options={options}
      styles={customStyles}
      value={selectedOption}
      onChange={onModelChange}
      openMenuOnFocus
      placeholder={null}
      components={{
        IndicatorSeparator: () => null,
      }}
      theme={(theme) => ({
        ...theme,
        borderRadius: 4,
        zIndex: 3,
        colors: {
          ...theme.colors,
          primary25: 'rgb(0, 115, 157, 0.1)',
          primary: 'rgba(0, 115, 157, 0.5)',
          neutral30: 'rgba(0, 115, 157, 0.5)',
        },
      })}
    />
  );
};

export const VehicleYearSelect = ({ year, yearAttr, vinAttr, onchange, ...props }) => {
  const { getValueByAttr, setValueByAttr } = useContext(FormContext);
  const [selectedOption, setSelectedOption] = useState(
      year ? { value: year, label: year } : props.value
  );
  const vin = getValueByAttr(vinAttr);

  const onYearChange = (selectValue) => {
    setSelectedOption({ value: selectValue, label: selectValue });
    if (onchange) {
      onchange(selectValue);
    }
  };

  return (
    <YearPicker
      {...props}
      yearAttr={yearAttr}
      value={props.value || year}
      onChange={onYearChange}
      disabled={vin && vin.length > 16}
      aria-label="Select vehicle year"
    />
  );
};

const customStyles = {
  control: (base) => ({
    ...base,
    '&:hover': {
      cursor: 'pointer',
    },
  }),
  menu: (styles) => ({ ...styles, zIndex: 999 }),
};
